// @flow
/* -----------------Globals--------------- */
import React, {
  useContext,
  useEffect,
} from 'react';
/* -----------------Third parties--------------- */
import API, { graphqlOperation } from '@aws-amplify/api';

/* -----------------Nextjs--------------- */
import Router, { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

/* -----------------Utils--------------- */
import {
  authenticated,
  key,
} from '../../components/src/common/authutils';
import env from '@components/env/relay-environment-web';
import {
  navigateToFlashcards,
} from '../../components/src/navigationUtils';
import {
  isEmptyList,
} from 'common/simpleUtils';
import invite from 'common/share/mutations/Invite';
import {
  studySetQuery, makeStudySetJsonLd,
  getCanonicalUrl,
  breadcrumbsQuery,
  constructJSONLDbreadcrumbs,
  constructStudysetMetaDescription,
} from '@components/head/jsonLdUtils';
import {
  getHrefAndPathForContent,
} from '@components/common/utils';
import {
  constructBreadcrumbs,
  type TBreadcrumbs,
} from '@components/common/appbarutils';

/* -----------------Context--------------- */
import { pageVariant, PageContext } from '@components/common/PageContext';

/* -----------------Types--------------- */
import { publishMode } from 'common/create/types';
import type {
  NextContext,
  StudySetDetails,
} from 'common/types';
import {
  pageType,
  tagType,
} from 'common/analytics/types';

/* -----------------Constants--------------- */
import { baseUrl } from 'common/urlConstants';

/* -----------------Child components--------------- */
import Loader from '@components/loading/StudySetDetailsSkeleton';
import StudySetUnAuth from '@components/studyContent/StudySetUnAuth';
import ErrorComponent from '@components/common/ErrorComponent';

const SSGIds = ['285566', '285567', '285632', '285563', '285436', '285462', '285463', '285464'];
const StudySetContainerAuth = dynamic(() => import('@components/studyContent/StudySetContainer'), {
  ssr: false,
  loading: () => <Loader />,
});
/* -----------------Component--------------- */

type Props = {
  cachedStudySetDetails: StudySetDetails,
  slug: *,
  status: string,
  cachedBreadcrumbs: TBreadcrumbs[],
}

const FlashcardsPage = (props: Props) => {
  const {
    cachedStudySetDetails,
    slug = [],
    status,
    cachedBreadcrumbs,
  } = props;

  const idFromURL = slug[0];

  console.log("slug", slug)

  const { setVariant } = useContext(PageContext);
  const isAuthenticated = authenticated(key.loggedIn);
  const { query, asPath } = useRouter();
  const { intentId } = query;
  const reload = React.useCallback(() => {
    const path = asPath.split('?')[0];
    Router.push(path);
  }, [asPath]);

  React.useEffect(() => {
    const callInvite = async (id) => {
      if (!env.isRehydrated()) {
        await env.hydrate();
      }
      try {
        await invite(id, env);
        reload();
      } catch (error) {
        reload();
      }
    };
    if (intentId && isAuthenticated) {
      callInvite(intentId);
    }
  }, [intentId, isAuthenticated, reload]);

  useEffect(() => {
    if (cachedStudySetDetails) {
      const { studySet: { name, numericId, accessType } } = cachedStudySetDetails;
      const params = {
        id: numericId,
        title: name,
        accessType,
        tagType: [tagType['g-optimize']],
      };
      if (typeof setVariant === 'function') {
        setVariant(pageVariant.app,
          pageType['study set'], params);
      }
      const {
        slug: finalSlug,
      } = getHrefAndPathForContent('STUDY_SET', numericId, name);

      if (finalSlug && !asPath.includes(finalSlug) && !query.intentId) {
        navigateToFlashcards(
          numericId,
          finalSlug,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedStudySetDetails]);
  useEffect(() => {
    if (typeof setVariant === 'function') {
      setVariant(pageVariant.app);
    }
  }, [setVariant]);

  if (!isAuthenticated && status) {
    return <ErrorComponent statusCode={Number(status)} />;
  }

  if (isEmptyList(slug) || intentId) {
    return <Loader />;
  }

  return (
    <>
      {isAuthenticated ? (
        <StudySetContainerAuth
          numericId={Number(idFromURL)}
          intentId={query.intentId}
          cachedStudySetDetails={cachedStudySetDetails}
          cachedBreadcrumbs={cachedBreadcrumbs}
        />
      ) : (
        <StudySetUnAuth
          cachedStudySetDetails={cachedStudySetDetails}
          intentId={query.intentId}
          cachedBreadcrumbs={cachedBreadcrumbs}
        />
      )}
    </>
  );
};

export const getStaticPaths = async () => {
  const paths = SSGIds.map < any > (id => ({
    params: {
      slug: [id],
    },
  }));
  return { paths, fallback: true };
};

export const getStaticProps = async (context: NextContext) => {
  const { slug } = context.params;

  console.log("context.params", context.params)
  const id = Number(slug[0]);

  try {
    const res = await API.graphql(graphqlOperation(studySetQuery,
      { id, publishMode: publishMode.PUBLISHED }));
    const { studySet } = res.data.studySetDetails;
    const metaDescription = constructStudysetMetaDescription(studySet);
    const courseJsonLd = JSON.stringify(makeStudySetJsonLd(studySet, metaDescription));
    const cachedStudySetDetails = res.data.studySetDetails;
    const {
      studySet: {
        name,
        numericId,
        id: contentId,
        contentType,
        itemsCount: {
          numFacts,
        },
        terms: {
          edges,
        },
      },
    } = cachedStudySetDetails;
    if (!numFacts) {
      return { notFound: true };
    }
    const canonicalUrl = getCanonicalUrl(contentType, numericId, name);
    const itemImages = edges.slice(0, 5).reduce((acc, termObj) => {
      const imagesTermArr = termObj.node.term.images;
      const factImages = [];
      termObj.node.term.facts.forEach(({
        fact: {
          image,
        },
      }) => {
        if (image) {
          factImages.push(image);
        }
      });
      const finalImages = [...imagesTermArr, ...factImages];
      return acc.concat(finalImages);
    }, []);
    const ogImageUrl = itemImages[0] ? itemImages[0].url : `${baseUrl}static/og/og_study_set.png`;
    const ogImageWidth = itemImages[0] ? itemImages[0].width : 1200;
    const ogImageHeight = itemImages[0] ? itemImages[0].height : 630;

    const title = `${name} Flashcards | Memorang`;
    const {
      data: {
        breadCrumbs = [],
      },
    } = await API.graphql(graphqlOperation(breadcrumbsQuery,
      { contentId }));
    const jsonLdBreadcrumbs = constructJSONLDbreadcrumbs(breadCrumbs);
    const cachedBreadcrumbs = constructBreadcrumbs(breadCrumbs);

    return {
      props: {
        slug,
        cachedStudySetDetails,
        cachedBreadcrumbs,
        meta: {
          title,
          description: metaDescription,
          url: canonicalUrl,
          imageProps: {
            imageUrl: ogImageUrl,
            imageAlt: name,
            imageWidth: ogImageWidth,
            imageHeight: ogImageHeight,
          },
          courseJsonLd,
          jsonLdBreadcrumbs,
        },
      },
    };
  } catch (errorObj) {
    const status = errorObj.errors ? errorObj.errors[0].message : 404;

    return {
      props: {
        slug,
        status,
      },
    };
  }
};

export default FlashcardsPage;
